import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { DataTable } from "components/ui/DataTable";
import { Notice, NoticeText } from "components/ui/Notice";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import {
  useClearRecallState,
  useSetRecallState,
} from "features/immunizations/hooks/useRecallState";
import { useVaccineRecall } from "features/immunizations/hooks/useVaccineRecall";
import {
  daysOverdue,
  isRecallOverdue,
  recallPatientName,
  splitRecallByCertainty,
} from "features/immunizations/utils/recall";
import {
  formatRecallState,
  recallStateForCourse,
} from "features/immunizations/utils/recallState";
import { formatCourseName } from "features/immunizations/utils/schedule";
import { formatDateWithDashes } from "utils/dateFormatter";
import { pathGenerators } from "utils/routes";

import { RecordRecallContactDialog } from "./RecordRecallContactDialog";
import s from "./VaccineRecallTable.module.css";

const courseLabel = (slot) => `${formatCourseName(slot)} dose ${slot.doseNumber}`;

/**
 * Children in this practice with an outstanding dose (KIN-1858).
 *
 * The other half of KIN-1811's front end: the patient schedule answers "what is this child
 * due", this answers "who in this practice is behind". Both are read-only and derived on
 * request, so nothing here is claimed or marked done — the list is a snapshot, and acting
 * on a row means opening the patient.
 *
 * The two certainty groups are kept in separate tables rather than sorted together. No
 * immunisation history migrates into this system, so a patient whose record is incomplete
 * looks due for everything; those rows are worth chasing but are candidates to confirm,
 * not confirmed work, and mixing them in would have a practice ringing parents whose
 * children are already vaccinated.
 */
export const VaccineRecallTable = ({
  dueBy,
  includeUncertainHistory,
  includeNotified = true,
}) => {
  const navigate = useNavigate();

  // { patientId, patientName, slot } — which course of which child the dialog is about.
  const [contactTarget, setContactTarget] = useState(null);

  const {
    data: recallList = [],
    isLoading,
    isError,
    refetch,
  } = useVaccineRecall({
    dueBy: dueBy || undefined,
    includeUncertainHistory,
    includeNotified,
  });

  const { mutate: setRecallState, isPending: isSaving } = useSetRecallState();
  const { mutate: clearRecallState } = useClearRecallState();

  const { confirmed, toConfirm } = useMemo(
    () => splitRecallByCertainty(recallList),
    [recallList]
  );

  const columns = useMemo(
    () => [
      {
        id: "patient",
        header: "Patient",
        width: "200px",
        render: (item) => recallPatientName(item) || "-",
      },
      {
        id: "dateOfBirth",
        header: "Date of birth",
        width: "130px",
        render: (item) => formatDateWithDashes(item.dateOfBirth) || "-",
      },
      {
        id: "mobilePhone",
        header: "Mobile",
        width: "150px",
        render: (item) => item.mobilePhone || "-",
      },
      {
        id: "outstanding",
        header: "Outstanding",
        /*
         * The column the call is actually about, and now the column it is made from: each
         * outstanding course is its own control, because contact state is per course
         * (KIN-1890). A parent chased about MMR has not been chased about the 6-in-1, and
         * a single button for the whole row could not say which.
         */
        width: "730px",
        wrap: true,
        render: (item) => {
          if (!item.outstanding?.length) return "-";

          return (
            <div className={s.courseList}>
              {item.outstanding.map((slot) => {
                const state = recallStateForCourse(item, slot.courseKey);
                const stateLabel = formatRecallState(state, formatDateWithDashes);

                /*
                 * A worked course goes green, the way every other "done" pill in the app
                 * does; one nobody has touched stays neutral. The eye should land on what
                 * is still to do.
                 */
                const colors = state
                  ? PILL_COLORS.sentYes
                  : PILL_COLORS.reactionUnknown;

                return (
                  <button
                    key={`${slot.courseKey}-${slot.doseNumber}`}
                    type="button"
                    className={s.course}
                    title={
                      state?.note ||
                      `Record how this family was contacted about ${courseLabel(slot)}`
                    }
                    onClick={(event) => {
                      // The row itself opens the patient; this must not.
                      event.stopPropagation();
                      setContactTarget({
                        patientId: item.patientId,
                        patientName: recallPatientName(item),
                        slot,
                        state,
                      });
                    }}
                  >
                    <Pill
                      color={colors.color}
                      bg={colors.bg}
                      border={colors.border}
                    >
                      {courseLabel(slot)}
                      {!!stateLabel && ` · ${stateLabel}`}
                    </Pill>
                  </button>
                );
              })}
            </div>
          );
        },
      },
      {
        id: "earliestDueOn",
        header: "Earliest due",
        width: "130px",
        render: (item) => formatDateWithDashes(item.earliestDueOn) || "-",
      },
      {
        id: "status",
        header: "Status",
        width: "160px",
        // Read from the date rather than from the response: the backend has no OVERDUE
        // status — DUE means "you can give this now" whether it became giveable today or
        // a year ago. Same rule the patient schedule uses. Everything in this list is due
        // by the chosen date, so the only question a row answers is how late it is.
        render: (item) => {
          const overdue = isRecallOverdue(item);
          const colors = overdue
            ? PILL_COLORS.reactionYes
            : PILL_COLORS.reactionNo;

          return (
            <Pill color={colors.color} bg={colors.bg} border={colors.border}>
              {overdue ? `${daysOverdue(item)} days overdue` : "Due"}
            </Pill>
          );
        },
      },
    ],
    []
  );

  const openPatient = (row) =>
    navigate(pathGenerators.patient.immunizations(row.patientId));

  const closeContactDialog = () => setContactTarget(null);

  const saveContact = (request) =>
    setRecallState(
      {
        patientId: contactTarget.patientId,
        courseKey: contactTarget.slot.courseKey,
        request,
      },
      { onSuccess: closeContactDialog }
    );

  const clearContact = () =>
    clearRecallState(
      {
        patientId: contactTarget.patientId,
        courseKey: contactTarget.slot.courseKey,
      },
      { onSuccess: closeContactDialog }
    );

  return (
    <div className={s.container}>
      <DataTable
        columns={columns}
        data={confirmed}
        getRowId={(row) => row.patientId}
        isLoading={isLoading}
        isError={isError}
        onRetry={refetch}
        errorLabel="Could not load the recall list."
        onRowClick={openPatient}
        emptyTitle="Nobody is outstanding"
        emptyText="No child in this practice has a dose outstanding for this date. Try a later due-by date, or turn on families already contacted if they are being hidden."
      />

      {!!toConfirm.length && (
        <>
          <h4 className={s.subheading}>Candidates to confirm</h4>
          <Notice>
            <NoticeText>
              No immunisation history migrates into this system, so these patients
              look due for everything. Confirm what they have already had before
              treating any of it as outstanding.
            </NoticeText>
          </Notice>
          <DataTable
            columns={columns}
            data={toConfirm}
            getRowId={(row) => row.patientId}
            onRowClick={openPatient}
          />
        </>
      )}

      <RecordRecallContactDialog
        open={!!contactTarget}
        patientName={contactTarget?.patientName ?? ""}
        courseLabel={contactTarget ? courseLabel(contactTarget.slot) : ""}
        existingState={contactTarget?.state ?? null}
        onClose={closeContactDialog}
        onSave={saveContact}
        onClear={clearContact}
        isSaving={isSaving}
      />
    </div>
  );
};
